/**
 * BESTIX v5.0 — Magazine Article Data
 * Sample articles for the editorial magazine
 */

import type { ArticleCardProps } from '@/components/magazine';
import type { ArticleVerdictProps } from '@/components/magazine/ArticleVerdict';

export interface Article extends ArticleCardProps {
  content: string;
  verdict?: ArticleVerdictProps;
}

export const articles: Article[] = [
  {
    slug: 'bestix-is-not-a-ticket-site',
    title: 'Bestix Is Not a Ticket Site',
    subtitle: "It's a Night-Out Company",
    excerpt: "Nobody wakes up excited to 'purchase tickets.' They wake up wanting a story worth telling tomorrow. Here's what makes us different.",
    image: 'https://images.unsplash.com/photo-1470229722913-7c0e2dbbafd3?w=1200&q=80',
    category: 'Manifesto',
    readTime: 5,
    author: 'BESTIX Editorial',
    publishedAt: 'Jan 25, 2026',
    featured: true,
    content: `
      <p class="text-xl leading-relaxed">Nobody wakes up excited to "purchase tickets." They wake up wanting a story worth telling tomorrow. That distinction is everything to us.</p>
      
      <h2>The Moment Before the Moment</h2>
      <p>There's a feeling you get when you're getting ready for a show. The playlist is on, you're deciding what to wear, your friends are texting "leaving in 10." That anticipation? That's what we're selling. The ticket is just the key.</p>
      
      <p>Other platforms treat tickets like airline seats—a necessary commodity to get from A to B. We treat them like the opening chapter of a night that could become legendary.</p>
      
      <h2>Why We Built This</h2>
      <p>Every other ticket site feels like buying printer ink. Functional. Boring. Forgettable. We wanted to build something that captures the electricity of live events—before you even get there.</p>
      
      <blockquote>
        "The best nights of your life don't start with clicking 'Add to Cart.' They start with that spark of possibility."
      </blockquote>
      
      <h2>Our Promise</h2>
      <p>Every feature we build, every word we write, every pixel we place asks one question: "Does this make the night better?" If the answer is no, we don't ship it.</p>
      
      <p>Welcome to BESTIX. Let's go somewhere.</p>
    `,
    verdict: {
      rating: 'go',
      ratingText: 'BESTIX is for people who believe the night matters as much as the destination.',
      theMoment: 'When you realize tickets are just the beginning of the experience.',
      theMove: 'Explore our curated event picks and trust our Deal Score to find value.',
      theTax: 'Transparent fees, always. No surprises at checkout.',
      whoItsFor: [
        'Experience-seekers who want more than a transaction',
        'People who plan nights out like adventures',
        'Those who read reviews before committing'
      ],
      whoShouldSkip: [
        'Bulk buyers looking for pure price comparison',
        'Those who just want the cheapest possible option',
        'People who never read the event details'
      ],
    },
  },
  {
    slug: 'anatomy-of-a-legendary-concert',
    title: 'The Anatomy of a Legendary Concert',
    subtitle: 'What separates good from unforgettable',
    excerpt: 'After 200+ shows, we identified the 7 elements that make a concert legendary. Here\'s the formula.',
    image: 'https://images.unsplash.com/photo-1501281668745-f7f57925c138?w=1200&q=80',
    category: 'Why Go',
    readTime: 8,
    author: 'BESTIX Editorial',
    publishedAt: 'Jan 24, 2026',
    featured: false,
    content: `
      <p class="text-xl leading-relaxed">After attending over 200 concerts and interviewing dozens of music industry insiders, we've identified the seven elements that separate a "good show" from a "night you'll tell your grandkids about."</p>
      
      <h2>1. The Venue Match</h2>
      <p>A intimate acoustic set at Madison Square Garden falls flat. A metal show at a jazz club? Equally wrong. The magic happens when artist energy meets venue energy.</p>
      
      <h2>2. The Setlist Stakes</h2>
      <p>Legendary shows have a narrative arc. Opening with the hits is lazy. The best artists build tension, surprise you mid-set, and send you home with the anthem still ringing.</p>
      
      <h2>3. The Crowd Chemistry</h2>
      <p>You can't control this, but you can optimize for it. Floor tickets on a Saturday bring different energy than mezzanine seats on a Tuesday.</p>
      
      <h2>4. The Production Investment</h2>
      <p>Touring in support of a new album? Usually means new visuals, new staging, new moments. Mid-tour dates often have tighter production than opening nights.</p>
      
      <h2>5. The Spontaneous Moment</h2>
      <p>The cover nobody expected. The story between songs. The acknowledgment of the city you're in. These can't be manufactured—but venues with good acoustics and intimate sightlines increase the odds.</p>
      
      <h2>6. The Sound Check Reality</h2>
      <p>Outdoor venues with wind, basketball arenas with echo, theaters with perfect acoustics—know what you're getting into.</p>
      
      <h2>7. The Shared Experience</h2>
      <p>The strangers around you become characters in your story. This is why we recommend going even if friends bail.</p>
    `,
    verdict: {
      rating: 'go',
      ratingText: 'Apply this framework to any show and dramatically increase your odds of a legendary night.',
      theMoment: 'When you realize venue selection matters as much as artist selection.',
      theMove: 'Check our venue guides before buying tickets. Match the artist to the room.',
      whoItsFor: [
        'Concert veterans who want to level up',
        'First-timers planning a bucket-list show',
        'Anyone tired of "meh" experiences'
      ],
      whoShouldSkip: [
        'People who only care about being able to say they went',
        'Those who leave during the encore',
        'Anyone who thinks all shows are basically the same'
      ],
    },
  },
  {
    slug: 'floor-seats-vs-elevated-views',
    title: 'Floor Seats vs. Elevated Views',
    subtitle: 'The eternal debate, settled',
    excerpt: 'Floor isn\'t always better. After analyzing 50+ venues, here\'s when to go low and when to stay high.',
    image: 'https://images.unsplash.com/photo-1429962714451-bb934ecdc4ec?w=1200&q=80',
    category: 'Seat Snob',
    readTime: 6,
    author: 'BESTIX Editorial',
    publishedAt: 'Jan 23, 2026',
    featured: false,
    content: `
      <p class="text-xl leading-relaxed">The floor isn't always the best seat in the house. Sometimes it's the worst. Here's our definitive guide to choosing your vantage point.</p>
      
      <h2>When Floor Wins</h2>
      <ul>
        <li><strong>Hip-hop shows:</strong> Energy radiates from the stage outward. You want to feel the bass.</li>
        <li><strong>Intimate venues:</strong> Under 3,000 capacity, floor sightlines rarely suffer.</li>
        <li><strong>Artists who work the crowd:</strong> Springsteen, Garth Brooks, anyone who ventures off stage.</li>
      </ul>
      
      <h2>When Elevated Wins</h2>
      <ul>
        <li><strong>Production-heavy tours:</strong> Beyoncé, Taylor Swift, Coldplay—their visuals need perspective.</li>
        <li><strong>Stadium shows:</strong> Floor at a 60,000-seat venue often means staring at someone's phone.</li>
        <li><strong>Orchestral arrangements:</strong> Sound mixing is optimized for elevated positions.</li>
      </ul>
      
      <h2>The Sweet Spot Secret</h2>
      <p>Lower bowl, center sections, rows 10-20. Close enough for energy, elevated enough for production, centered for balanced sound. This is the insider move.</p>
    `,
    verdict: {
      rating: 'go-if',
      ratingText: 'Floor is great IF you know what you\'re getting into. Do your homework.',
      theMoment: 'When you realize the $200 nosebleeds had a better view than your $500 floor seats.',
      theMove: 'Check YouTube for fan footage from different sections before committing.',
      theTax: 'Floor premiums average 40% more. Often not worth it for production shows.',
      whoItsFor: [
        'Detail-oriented planners who research venue layouts',
        'People willing to pay for the right experience',
        'Those who care about seeing the full production'
      ],
      whoShouldSkip: [
        'Status-seekers who just want to say they were on the floor',
        'People who assume expensive = better',
        'Anyone under 5\'6" at GA floor shows'
      ],
    },
  },
  {
    slug: 'last-minute-tickets-playbook',
    title: 'The Last-Minute Tickets Playbook',
    subtitle: 'How to score deals 48 hours before showtime',
    excerpt: 'Patience pays. Here\'s exactly when prices drop and how to capitalize without missing out.',
    image: 'https://images.unsplash.com/photo-1516450360452-9312f5e86fc7?w=1200&q=80',
    category: 'Ticket Confidence',
    readTime: 7,
    author: 'BESTIX Editorial',
    publishedAt: 'Jan 22, 2026',
    featured: false,
    content: `
      <p class="text-xl leading-relaxed">The secondary market is a game of chicken. Sellers need to offload inventory before the doors open. Here's how to play it.</p>
      
      <h2>The 48-Hour Window</h2>
      <p>Prices typically start dropping 48 hours before showtime. Sellers panic. Supply exceeds demand. This is your moment.</p>
      
      <h2>The 4-Hour Sweet Spot</h2>
      <p>For truly aggressive deals, check prices 4 hours before the event. Sellers will slash prices rather than eat the tickets entirely.</p>
      
      <h2>The Risk Matrix</h2>
      <p>Not every show drops. Sold-out shows with verified waitlists rarely discount. Mid-week shows at 70% capacity? Target rich environment.</p>
      
      <h2>Platform Timing</h2>
      <p>Different platforms update prices at different intervals. Check multiple sources. Our Deal Score algorithm accounts for this.</p>
    `,
    verdict: {
      rating: 'go-if',
      ratingText: 'Last-minute buying works IF you can tolerate risk and stay flexible.',
      theMoment: 'When you score 50% off face value 2 hours before doors open.',
      theMove: 'Set price alerts and check our "Tonight" section for real-time deals.',
      theTax: 'You might pay full price anyway if the show sells through. Budget for both outcomes.',
      whoItsFor: [
        'Flexible locals who can commit last-minute',
        'Risk-tolerant bargain hunters',
        'Solo attendees who don\'t need to coordinate groups'
      ],
      whoShouldSkip: [
        'Group planners who need firm commitments',
        'People traveling for the show',
        'Anyone who\'ll be devastated if they miss out'
      ],
    },
  },
  {
    slug: 'super-bowl-experience-guide',
    title: 'Your First Super Bowl: The Complete Guide',
    subtitle: 'Is it worth the price tag?',
    excerpt: 'At $5,000+ per ticket, the Super Bowl promises an elite experience. We attended to find out if it delivers.',
    image: 'https://images.unsplash.com/photo-1566577739112-5180d4bf9390?w=1200&q=80',
    category: 'Why Go',
    readTime: 10,
    author: 'BESTIX Editorial',
    publishedAt: 'Jan 20, 2026',
    featured: false,
    content: `
      <p class="text-xl leading-relaxed">The Super Bowl isn't just a football game. It's a three-day festival of excess, celebrity sightings, and corporate hospitality that defies rational economic analysis.</p>
      
      <h2>The Real Cost</h2>
      <p>Ticket prices are just the beginning. Factor in flights, hotels (often 5x normal rates), and the "Super Bowl tax" on everything from parking to pretzels.</p>
      
      <h2>What You're Really Paying For</h2>
      <p>The halftime show production. The crowd energy during close games. The story you'll tell for decades. The people you'll meet in the stadium bars.</p>
      
      <h2>The Alternatives</h2>
      <p>Championship games offer 80% of the atmosphere at 20% of the cost. Conference finals are the sweet spot for most fans.</p>
    `,
    verdict: {
      rating: 'go-if',
      ratingText: 'The Super Bowl is worth it IF your team is playing or it\'s a genuine bucket list item.',
      theMoment: 'When confetti falls and you\'re surrounded by 70,000 people sharing the same experience.',
      theMove: 'Book hotels before the teams are set. Prices only go up.',
      theTax: 'All-in costs average $8,000-$12,000 for a modest experience.',
      whoItsFor: [
        'Die-hard fans whose team is playing',
        'Bucket-listers with discretionary budget',
        'Corporate entertainment with expense accounts'
      ],
      whoShouldSkip: [
        'Casual fans on a budget',
        'People who prefer the TV experience',
        'Anyone who hates crowds and long lines'
      ],
    },
  },
  {
    slug: 'broadway-vs-touring-shows',
    title: 'Broadway vs. Touring Shows',
    subtitle: 'When NYC is worth the trip',
    excerpt: 'The touring production hits your city next month. Should you wait or spring for the Broadway original?',
    image: 'https://images.unsplash.com/photo-1507676184212-d03ab07a01bf?w=1200&q=80',
    category: 'Show Types',
    readTime: 6,
    author: 'BESTIX Editorial',
    publishedAt: 'Jan 18, 2026',
    featured: false,
    content: `
      <p class="text-xl leading-relaxed">Hamilton in New York or Hamilton in Cleveland? The show is the same. The experience is not.</p>
      
      <h2>When Broadway Wins</h2>
      <ul>
        <li><strong>Original cast:</strong> Star power matters. Check who's currently performing.</li>
        <li><strong>Intimate theaters:</strong> Broadway houses are often smaller than touring venues.</li>
        <li><strong>The ritual:</strong> Pre-show dinner in the Theater District. Walking out onto Times Square.</li>
      </ul>
      
      <h2>When Touring Wins</h2>
      <ul>
        <li><strong>Price:</strong> Often 40-60% cheaper than Broadway seats.</li>
        <li><strong>Fresh productions:</strong> Touring casts are often sharper—they're proving themselves.</li>
        <li><strong>Convenience:</strong> No flights, no hotels, no Broadway premium.</li>
      </ul>
    `,
    verdict: {
      rating: 'go-if',
      ratingText: 'Broadway is worth it IF the original cast is performing or you\'re making it a NYC trip.',
      theMoment: 'Walking into a historic theater where legends have performed.',
      theMove: 'Check Broadway cast announcements. Time your visit with star power.',
      theTax: 'Broadway premium averages 50%+ over touring prices.',
      whoItsFor: [
        'Theater enthusiasts who appreciate the full NYC experience',
        'Those planning a broader NYC trip anyway',
        'Fans of specific performers in the current cast'
      ],
      whoShouldSkip: [
        'Budget-conscious first-time theatergoers',
        'People who just want to see the show',
        'Anyone who gets stressed by NYC logistics'
      ],
    },
  },
];

export function getArticleBySlug(slug: string): Article | undefined {
  return articles.find(a => a.slug === slug);
}

export function getArticlesByCategory(category: string): Article[] {
  const categorySlug = category.toLowerCase().replace(/\s+/g, '-');
  return articles.filter(a => a.category.toLowerCase().replace(/\s+/g, '-') === categorySlug);
}

export function getFeaturedArticles(): Article[] {
  return articles.filter(a => a.featured);
}

export function getRecentArticles(limit = 6): Article[] {
  return articles.slice(0, limit);
}
